/**
 * Delivery zones per market, for the admin forms that price them. Covers the US
 * (five zones) and Malaysia (two).
 *
 * Zoned delivery is charged by the customer's shipping-address state. The state to
 * zone mapping that checkout uses lives in web-backend (utils/us-delivery-zones.ts);
 * this copy only carries what the admin needs to label the fields. Every market's
 * fees share product_country.delivery_zone_fees, keyed "1".."n".
 */
export interface DeliveryZoneDef {
  zone: 1 | 2 | 3 | 4 | 5;
  label: string;
  states: string;
}

export const US_DELIVERY_ZONES: readonly DeliveryZoneDef[] = [
  {
    zone: 1,
    label: "Zone 1 — Midwest & Central",
    states: "IL, IN, IA, KS, MI, MN, MO, NE, ND, OH, SD, WI",
  },
  {
    zone: 2,
    label: "Zone 2 — East Coast & Northeast",
    states: "CT, DE, DC, ME, MD, MA, NH, NJ, NY, PA, RI, VT",
  },
  {
    zone: 3,
    label: "Zone 3 — South & Southeast",
    states: "AL, AR, FL, GA, KY, LA, MS, NC, OK, SC, TN, TX, VA, WV",
  },
  {
    zone: 4,
    label: "Zone 4 — West Coast & Mountain West",
    states: "AZ, CA, CO, ID, MT, NV, NM, OR, UT, WA, WY",
  },
  {
    zone: 5,
    label: "Zone 5 — Offshore & Territories",
    states: "AK, HI, PR, VI, GU, AS, MP",
  },
];

export const MY_DELIVERY_ZONES: readonly DeliveryZoneDef[] = [
  {
    zone: 1,
    label: "West Malaysia",
    states:
      "Johor, Kedah, Kelantan, Melaka, Negeri Sembilan, Pahang, Penang, Perak, Perlis, Selangor, Terengganu, Kuala Lumpur, Putrajaya",
  },
  {
    zone: 2,
    label: "Sabah & Sarawak",
    states: "Sabah, Sarawak, Labuan",
  },
];

/** A market's delivery zones; empty for a market charged a single fee. */
export const deliveryZonesFor = (iso2: string): readonly DeliveryZoneDef[] =>
  iso2 === "US" ? US_DELIVERY_ZONES : iso2 === "MY" ? MY_DELIVERY_ZONES : [];

/** The form-control name for a zone's fee, e.g. "zoneFee1". */
export const zoneControlName = (zone: number) => `zoneFee${zone}`;

/** Every market's zone-fee control names, for code that touches whichever exist. */
export const ALL_ZONE_CONTROL_NAMES: readonly string[] = [
  ...new Set([...US_DELIVERY_ZONES, ...MY_DELIVERY_ZONES].map((z) => zoneControlName(z.zone))),
];

/** Zone fees keyed by zone number, as stored in delivery_zone_fees. */
export type ZoneFees = Partial<Record<"1" | "2" | "3" | "4" | "5", number>>;

/**
 * The single fee kept alongside zone fees: the highest of them. Readers that
 * predate zones use it, and checkout falls back to it when an address's state
 * can't be matched — so it must never be lower than any zone.
 */
export const highestZoneFee = (fees: ZoneFees): number =>
  Math.max(0, ...Object.values(fees).map((v) => Number(v) || 0));
